import type { Locale } from '@/i18n'

/** Codes pays pour le SELECT D'ADRESSE — liste exhaustive ISO 3166-1 alpha-2
 *  élargie de quelques codes exceptionnellement réservés utilisés en pratique
 *  (AC Ascension, CP Clipperton, DG Diego Garcia, EA Ceuta/Melilla, IC Canaries,
 *  TA Tristan da Cunha, XK Kosovo). La `value` renvoyée au back est le code ISO. */
export const COUNTRY_CODES_ADDRESS = [
  'AF', 'ZA', 'AX', 'AL', 'DZ', 'DE', 'AD', 'AO', 'AQ', 'AG',
  'SA', 'AR', 'AM', 'AW', 'AC', 'AU', 'AT', 'AZ', 'BS', 'BH',
  'BD', 'BB', 'BE', 'BZ', 'BJ', 'BT', 'BY', 'MM', 'BO', 'BQ',
  'BA', 'BW', 'BR', 'BN', 'BG', 'BF', 'BI', 'KH', 'CM', 'CA',
  'CV', 'CF', 'EA', 'CL', 'CN', 'CY', 'CO', 'KM', 'CG', 'CD',
  'CK', 'KP', 'KR', 'CR', 'CI', 'HR', 'CU', 'CW', 'DK', 'DG',
  'DJ', 'DO', 'DM', 'EG', 'SV', 'AE', 'EC', 'ER', 'ES', 'EE',
  'SZ', 'US', 'ET', 'FO', 'FJ', 'FI', 'FR', 'GA', 'GM', 'GE',
  'GH', 'GI', 'GR', 'GD', 'GL', 'GT', 'GN', 'GQ', 'GW', 'GY',
  'HT', 'HN', 'HK', 'HU', 'BV', 'CP', 'IM', 'IC', 'HM', 'IN',
  'ID', 'IR', 'IQ', 'IE', 'IS', 'IL', 'IT', 'JM', 'JP', 'JE',
  'JO', 'KZ', 'KE', 'KG', 'KI', 'XK', 'KW', 'LA', 'LS', 'LV',
  'LB', 'LR', 'LY', 'LI', 'LT', 'LU', 'MO', 'MK', 'MG', 'MY',
  'MW', 'MV', 'ML', 'FK', 'MT', 'MP', 'MA', 'MH', 'MU', 'MR',
  'MX', 'FM', 'MD', 'MC', 'MN', 'ME', 'MZ', 'NA', 'NR', 'NP',
  'NI', 'NE', 'NG', 'NU', 'NO', 'NC', 'NZ', 'OM', 'UG', 'UZ',
  'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'NL', 'PE', 'PH', 'PN',
  'PL', 'PF', 'PT', 'QA', 'RO', 'GB', 'RU', 'RW', 'EH', 'BL',
  'KN', 'MF', 'SX', 'PM', 'VC', 'SH', 'LC', 'SB', 'WS', 'SM',
  'ST', 'SN', 'RS', 'SC', 'SL', 'SG', 'SK', 'SI', 'SO', 'SD',
  'SS', 'LK', 'SE', 'CH', 'SR', 'SJ', 'SY', 'TJ', 'TW', 'TZ',
  'TD', 'CZ', 'TF', 'TH', 'TL', 'TG', 'TO', 'TT', 'TA', 'TN',
  'TM', 'TR', 'TV', 'UA', 'UY', 'VU', 'VA', 'VE', 'VG', 'VI',
  'VN', 'WF', 'YE', 'ZM', 'ZW',
] as const

export type AddressCountryCode = typeof COUNTRY_CODES_ADDRESS[number]

/** Destination « Pack Europe » : pays UE/EEE/Suisse couverts (liste métier). */
export const COUNTRY_CODES_DESTINATION_EUROPE = [
  'DE', 'AT', 'BE', 'BG', 'CY', 'HR', 'DK', 'ES', 'EE', 'FI',
  'GR', 'HU', 'IE', 'IS', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT',
  'NO', 'NL', 'PL', 'PT', 'RO', 'SM', 'SK', 'SI', 'SE', 'CH',
  'CZ', 'VA',
] as const

/** Destination « Pack Monde » : liste métier des pays couverts (172 entrées).
 *  Ordre alphabétique FR de la référence métier ; le SELECT re-trie par libellé
 *  localisé à l'affichage. La plupart sont des codes ISO 3166-1 alpha-2 ; trois
 *  entrées héritées n'ont pas de code ISO standard et utilisent des codes métier
 *  (`ANHH`, `PORT-OI`, `ZANZ`), libellés via COUNTRY_FALLBACK_NAMES. */
export const COUNTRY_CODES_DESTINATION_WORLD = [
  'ZA', 'AL', 'DZ', 'AO', 'AG', 'ANHH', 'SA', 'AR', 'AM', 'AU',
  'AZ', 'BS', 'BH', 'BD', 'BB', 'BZ', 'BJ', 'BT', 'MM', 'BO',
  'BA', 'BW', 'BR', 'BN', 'BF', 'BI', 'KH', 'CM', 'CA', 'CV',
  'CF', 'CL', 'CN', 'CY', 'CO', 'KM', 'CG', 'CD', 'KR', 'CR',
  'CI', 'DJ', 'DO', 'DM', 'EG', 'SV', 'AE', 'EC', 'ER', 'SZ',
  'US', 'ET', 'FJ', 'GA', 'GM', 'GE', 'GH', 'GD', 'GL', 'GT',
  'GN', 'GQ', 'GW', 'GY', 'HT', 'HN', 'HK', 'PORT-OI', 'IN', 'ID',
  'IQ', 'IL', 'JM', 'JP', 'JO', 'KZ', 'KE', 'KG', 'KI', 'XK',
  'KW', 'LA', 'LS', 'LB', 'LY', 'MO', 'MK', 'MG', 'MY', 'MW',
  'MV', 'ML', 'FK', 'MP', 'MA', 'MH', 'MU', 'MR', 'MX', 'FM',
  'MD', 'MC', 'MN', 'ME', 'MZ', 'NA', 'NR', 'NP', 'NI', 'NE',
  'NG', 'NC', 'NZ', 'OM', 'UG', 'UZ', 'PK', 'PW', 'PS', 'PA',
  'PG', 'PY', 'PE', 'PH', 'PN', 'PF', 'QA', 'GB', 'RW', 'EH',
  'BL', 'KN', 'SH', 'LC', 'MF', 'VC', 'SB', 'WS', 'ST', 'SN',
  'RS', 'SC', 'SL', 'SG', 'SO', 'LK', 'SR', 'TJ', 'TW', 'TZ',
  'TD', 'TF', 'TH', 'TL', 'TG', 'TO', 'TT', 'TN', 'TM', 'TR',
  'TV', 'UY', 'VU', 'VE', 'VG', 'VI', 'VN', 'WF', 'YE', 'ZM',
  'ZANZ', 'ZW',
] as const

/** Codes pays pour le SELECT INDICATIF TÉLÉPHONIQUE — liste curée des pays
 *  pour lesquels un indicatif (+XX) est mappé dans COUNTRY_DIAL_CODES. Étendre
 *  cette liste implique d'ajouter l'indicatif correspondant ci-dessous. */
export const COUNTRY_CODES_PHONE = [
  'FR',
  'BE', 'CH', 'LU', 'ES', 'IT', 'DE', 'PT', 'GB', 'IE', 'NL',
  'PL', 'AT', 'DK', 'SE', 'NO', 'FI', 'GR', 'CZ', 'RO', 'HU',
  'MA', 'DZ', 'TN', 'SN', 'CI', 'CM',
  'US', 'CA', 'BR', 'MX',
  'CN', 'JP', 'KR', 'IN', 'TR', 'LB',
] as const

export type PhoneCountryCode = typeof COUNTRY_CODES_PHONE[number]

export const DEFAULT_COUNTRY = 'FR' as const

/** Indicatifs téléphoniques internationaux par code pays (E.164). Maintenu en
 *  parallèle de COUNTRY_CODES_PHONE — toute extension doit ajouter l'indicatif
 *  correspondant ici. US et CA partagent le +1 (NANP). */
export const COUNTRY_DIAL_CODES: Record<PhoneCountryCode, string> = {
  FR: '+33',
  BE: '+32', CH: '+41', LU: '+352', ES: '+34', IT: '+39', DE: '+49',
  PT: '+351', GB: '+44', IE: '+353', NL: '+31',
  PL: '+48', AT: '+43', DK: '+45', SE: '+46', NO: '+47', FI: '+358',
  GR: '+30', CZ: '+420', RO: '+40', HU: '+36',
  MA: '+212', DZ: '+213', TN: '+216', SN: '+221', CI: '+225', CM: '+237',
  US: '+1', CA: '+1', BR: '+55', MX: '+52',
  CN: '+86', JP: '+81', KR: '+82', IN: '+91', TR: '+90', LB: '+961',
}

/** Libellés de secours pour les codes que `Intl.DisplayNames(type:'region')`
 *  ne reconnaît pas systématiquement selon les navigateurs (codes ISO
 *  exceptionnellement réservés et transitionnels). */
export const COUNTRY_FALLBACK_NAMES: Record<string, Record<Locale, string>> = {
  AC: { fr: "Île de l'Ascension",  en: 'Ascension Island'  },
  CP: { fr: 'Île Clipperton',      en: 'Clipperton Island' },
  DG: { fr: 'Diego Garcia',        en: 'Diego Garcia'      },
  EA: { fr: 'Ceuta et Melilla',    en: 'Ceuta & Melilla'   },
  IC: { fr: 'Îles Canaries',       en: 'Canary Islands'    },
  TA: { fr: 'Tristan da Cunha',    en: 'Tristan da Cunha'  },
  XK: { fr: 'Kosovo',              en: 'Kosovo'            },
  // Codes métier « Pack Monde » sans équivalent ISO 3166-1 alpha-2 standard.
  ANHH: { fr: 'Antilles néerlandaises',                en: 'Netherlands Antilles'               },
  'PORT-OI': { fr: "Îles portugaises de l'océan Indien", en: 'Portuguese Islands of the Indian Ocean' },
  ZANZ: { fr: 'Zanzibar',                              en: 'Zanzibar'                           },
}

/** Convertit un code ISO 3166-1 alpha-2 en drapeau emoji via les Regional
 *  Indicator Symbols (U+1F1E6..U+1F1FF). Cross-platform : natif sur macOS/iOS
 *  et la plupart d'Android ; sur Windows < 11 le rendu reste les 2 lettres. */
export function flagEmoji(code: string): string {
  if (code.length !== 2) return ''
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
}

/** Résout le libellé localisé d'un code pays : Intl natif d'abord, sinon
 *  fallback dictionnaire, sinon le code brut. */
export function countryLabel(code: string, locale: Locale): string {
  try {
    const native = new Intl.DisplayNames([locale], { type: 'region' }).of(code)
    // Intl renvoie parfois le code lui-même quand il ne connaît pas l'entrée
    if (native && native !== code) return native
  } catch {
    // Intl lève une RangeError sur les codes non standard (codes métier des
    // territoires hérités) — on retombe sur le dictionnaire ci-dessous.
  }
  return COUNTRY_FALLBACK_NAMES[code]?.[locale] ?? code
}
